/* ==========================================================================
   VoxTune — Global Audio Engine
   ONE audio element shared by every before/after player and "Hear it" button.
   Conditionally loaded (only when a section that needs audio is present).
   Registered as VoxTune module 'audio'.

   API: var a = VoxTune.use('audio');
     a.load(id, {before, after, name})  — register a track pair
     a.play(id, which)                  — which = 'before' | 'after'
     a.toggle(id, which)
     a.pause()
     a.seek(fraction)                   — 0..1
     a.on(...) via VoxTune events: 'audio:play','audio:pause','audio:time','audio:ended'
   ========================================================================== */
(function (w) {
  'use strict';
  var VT = w.VoxTune;
  if (!VT) return;

  VT.module('audio', function (VT) {
    var el = null;             // single <audio>
    var tracks = {};           // id -> {before, after, name}
    var current = { id: null, which: null };

    function audio() {
      if (el) return el;
      el = new Audio();
      el.preload = 'none';     // mobile: don't fetch until play
      el.crossOrigin = 'anonymous';
      el.addEventListener('timeupdate', function () {
        VT.emit('audio:time', { id: current.id, which: current.which, time: el.currentTime, duration: el.duration || 0, fraction: el.duration ? el.currentTime / el.duration : 0 });
      });
      el.addEventListener('ended', function () {
        VT.emit('audio:ended', { id: current.id, which: current.which });
      });
      el.addEventListener('play', function () { VT.emit('audio:play', { id: current.id, which: current.which }); });
      el.addEventListener('pause', function () { VT.emit('audio:pause', { id: current.id, which: current.which }); });
      return el;
    }

    function load(id, data) { if (id) tracks[id] = Object.assign(tracks[id] || {}, data || {}); }

    function srcFor(id, which) {
      var t = tracks[id]; if (!t) return null;
      return which === 'before' ? (t.before || t.after) : (t.after || t.before);
    }

    function play(id, which) {
      which = which || 'after';
      var src = srcFor(id, which);
      if (!src) return false;
      var a = audio();
      // A/B sync: same track, only Before/After changed → keep the playhead.
      if (current.id === id && current.which !== which && a.src) return swapWhich(id, which, true);
      var switching = current.id !== id || current.which !== which;
      if (switching) { a.src = src; current = { id: id, which: which }; }
      var p = a.play();
      if (p && p.catch) p.catch(function () { /* autoplay/interaction guard */ });
      VT.track('AudioPlayed', { id: id, which: which });
      return true;
    }

    // Swap Before<->After for the active track without losing the playhead.
    // The before/after files are time-aligned, so we keep currentTime (clamped
    // to the new duration) and resume only if it was already playing.
    function swapWhich(id, which, forcePlay) {
      var src = srcFor(id, which);
      if (!src) return false;
      var a = audio();
      var t = a.currentTime || 0;
      var wasPlaying = forcePlay || !a.paused;
      current = { id: id, which: which };
      var restore = function () {
        var dur = (isFinite(a.duration) && a.duration) ? a.duration : t;
        try { a.currentTime = Math.min(t, dur); } catch (e) {}
        if (wasPlaying) { var p = a.play(); if (p && p.catch) p.catch(function () {}); }
        else { VT.emit('audio:time', { id: id, which: which, time: a.currentTime, duration: a.duration || 0, fraction: a.duration ? a.currentTime / a.duration : 0 }); }
      };
      a.addEventListener('loadedmetadata', restore, { once: true });
      a.src = src;
      a.load();
      return true;
    }

    // Switch Before/After from the UI: preserves position + play/pause state.
    // Only acts on the active track; otherwise the caller's next play(id,which)
    // picks the right source (so first-play detection stays intact).
    function setWhich(id, which) {
      which = which || 'after';
      if (current.id !== id || current.which === which) return false;
      return swapWhich(id, which, false);
    }

    function pause() { if (el) el.pause(); }
    function isPlaying(id, which) { return el && !el.paused && current.id === id && (!which || current.which === which); }

    function toggle(id, which) {
      which = which || 'after';
      if (isPlaying(id, which)) { pause(); return false; }
      return play(id, which);
    }

    function seek(fraction) { if (el && el.duration) el.currentTime = Math.max(0, Math.min(1, fraction)) * el.duration; }

    // "Hear it" buttons ([data-vp-hear]) are handled by the Preview popup
    // module (assets/preview-player.js), which drives this single engine.

    // Pause audio whenever a louder context takes over (sound profile change).
    VT.on('audio:stop', pause);
    VT.on('sound:profile:enter', function (p) { if (p && p !== 'home') pause(); });

    return { load: load, play: play, pause: pause, toggle: toggle, seek: seek, setWhich: setWhich, isPlaying: isPlaying, get current() { return current; } };
  });
})(window);
